import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Truck, Search, Loader2, Plus, ArrowRight, CheckCircle2, X } from "lucide-react";

const TYPES = [
  { key: "tow", label: "Tow coordination", Icon: Truck },
  { key: "investigator", label: "Investigator assignment", Icon: Search },
];
const STATUS_META = {
  requested: { label: "Requested", cls: "bg-cyan-500/15 text-cyan-300", next: "in_progress" },
  in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-300", next: "done" },
  done: { label: "Done", cls: "bg-emerald-500/15 text-emerald-300", next: null },
  cancelled: { label: "Cancelled", cls: "bg-red-500/15 text-red-300", next: null },
};

// Attorney-facing field-services (tow / investigator) workflow for a case.
export default function ServiceRequestsPanel() {
  const { caseId } = useParams();
  const [rows, setRows] = useState(undefined);
  const [type, setType] = useState("tow");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/service-requests`); setRows(data.requests || []); }
    catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/service-requests`, { type, note });
      toast.success("Request created"); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const advance = async (id, status) => {
    try { await api.post(`/service-requests/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (rows === undefined) return <div className="mt-8 flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="service-requests-panel">
      <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Field Services</p>
      <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Tow & Investigator Requests</h2>
      <p className="text-xs text-slate-500 mt-1">Internal coordination — Crash Ready never contacts an outside vendor without your review.</p>

      <div className="mt-4 rounded-xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {TYPES.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setType(key)} data-testid={`sr-type-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${type === key ? "bg-cyan-500 text-slate-950 border-cyan-500" : "bg-slate-900 text-slate-300 border-slate-700"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="sr-note"
          placeholder="Details (location, timeframe, what to investigate)…"
          className="mt-2 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
        <button onClick={create} disabled={busy} data-testid="sr-create"
          className="mt-2 flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create request
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 ? <p className="text-sm text-slate-500">No field-service requests on this case.</p> :
          rows.map((r) => {
            const sm = STATUS_META[r.status] || STATUS_META.requested;
            return (
              <div key={r.request_id} data-testid={`sr-row-${r.request_id}`} className="rounded-xl bg-slate-900/50 border border-slate-800 p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{r.label}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sm.cls}`}>{sm.label}</span>
                    {r.source === "voice" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-cyan-300">via voice</span>}
                  </div>
                  {r.note && <div className="text-xs text-slate-400 mt-0.5">{r.note}</div>}
                  <div className="text-[11px] text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString()} · {r.requested_by_name}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {sm.next && (
                    <button onClick={() => advance(r.request_id, sm.next)} data-testid={`sr-advance-${r.request_id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-cyan-500/15 text-cyan-300 text-[11px] hover:bg-cyan-500/25">
                      {sm.next === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
                      {sm.next === "done" ? "Mark done" : "Start"}
                    </button>
                  )}
                  {r.status !== "cancelled" && r.status !== "done" && (
                    <button onClick={() => advance(r.request_id, "cancelled")} data-testid={`sr-cancel-${r.request_id}`}
                      className="p-1.5 rounded-full text-slate-500 hover:text-red-300 hover:bg-slate-800"><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
